<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { useMatterContextModalStore } from '../../stores/matterContextModalStore';
import FeedbackDialog from '../common/FeedbackDialog.vue';

const { t } = useI18n();
const store = useMatterContextModalStore();

const isEmpty = computed(() => !store.summary);

// Play the reveal animation only on the first render after extraction
// completes (consuming the store's one-shot flag), not on later tab switches.
const reveal = ref(store.consumeSummaryReveal());

// Summary text is LLM-generated markdown that may echo prompt-injected
// content from matter-context files; sanitize before v-html.
const renderedSummary = computed(() =>
    DOMPurify.sanitize(marked.parse(store.summary ?? '', { async: false }) as string),
);

const feedbackDialogOpen = ref(false);

// A feedback POST is outstanding — drives the spinner and disables both thumbs
// to guard against double-submits while in flight.
const isSubmitting = computed(() => store.summaryFeedbackSubmitting !== null);

const onCopy = async () => {
    try {
        await navigator.clipboard.writeText(store.summary);
        store.showToast(t('matterContext.summary.copiedToast'));
    } catch {
        store.showToast(t('matterContext.summary.copyFailedToast'));
    }
};

const onThumbsUp = () => {
    if (isSubmitting.value) return;
    void store.submitSummaryFeedback({ isPositive: true });
};

const onThumbsDown = () => {
    if (isSubmitting.value) return;
    feedbackDialogOpen.value = true;
};

const onFeedbackSubmit = (payload: { issueType: string; text: string }) => {
    feedbackDialogOpen.value = false;
    void store.submitSummaryFeedback({
        isPositive: false,
        issueReported: payload.issueType,
        feedbackDetails: payload.text,
    });
};

// Canceling the dialog still records a negative-feedback flag in the backend
// (dropping any issue type / details the user typed), but silently — no toast.
const onFeedbackSkip = () => {
    feedbackDialogOpen.value = false;
    void store.submitSummaryFeedback({ isPositive: false, silent: true });
};
</script>

<template>
    <div class="mc-summary-tab">
        <div v-if="isEmpty" class="mc-summary-placeholder">
            {{ t('matterContext.summary.placeholder') }}
        </div>

        <template v-else>
            <div v-if="store.summaryUpdating" class="mc-summary-updating" role="status">
                <span class="material-icons-outlined mc-summary-updating-icon">auto_awesome</span>
                {{ t('matterContext.summary.updating') }}
            </div>
            <!-- eslint-disable vue/no-v-html -- renderedSummary sanitizes via DOMPurify -->
            <div
                class="mc-summary-text"
                :class="{ 'mc-summary-cascade': reveal, 'mc-summary-glow': reveal }"
                v-html="renderedSummary"
            ></div>
            <!-- eslint-enable vue/no-v-html -->
            <div class="mc-summary-feedback-bar" :class="{ 'mc-summary-reveal-fade': reveal }">
                <div class="mc-summary-feedback-icons">
                    <button
                        type="button"
                        class="mc-summary-feedback-btn"
                        :title="t('matterContext.summary.copy')"
                        :aria-label="t('matterContext.summary.copy')"
                        @click="onCopy"
                    >
                        <span class="material-icons-outlined">content_copy</span>
                    </button>
                    <button
                        type="button"
                        class="mc-summary-feedback-btn"
                        :class="{
                            'mc-summary-feedback-btn-active':
                                store.summaryFeedback === 'positive',
                        }"
                        :title="t('matterContext.summary.goodResponse')"
                        :aria-label="t('matterContext.summary.goodResponse')"
                        :disabled="!store.canSubmitSummaryFeedback || isSubmitting"
                        @click="onThumbsUp"
                    >
                        <span
                            v-if="store.summaryFeedbackSubmitting === 'positive'"
                            class="mc-summary-feedback-spinner"
                            aria-hidden="true"
                        ></span>
                        <span
                            v-else
                            :class="
                                store.summaryFeedback === 'positive'
                                    ? 'material-icons'
                                    : 'material-icons-outlined'
                            "
                            >thumb_up</span
                        >
                    </button>
                    <button
                        type="button"
                        class="mc-summary-feedback-btn"
                        :class="{
                            'mc-summary-feedback-btn-active':
                                store.summaryFeedback === 'negative',
                        }"
                        :title="t('matterContext.summary.badResponse')"
                        :aria-label="t('matterContext.summary.badResponse')"
                        :disabled="!store.canSubmitSummaryFeedback || isSubmitting"
                        @click="onThumbsDown"
                    >
                        <span
                            v-if="store.summaryFeedbackSubmitting === 'negative'"
                            class="mc-summary-feedback-spinner"
                            aria-hidden="true"
                        ></span>
                        <span
                            v-else
                            :class="
                                store.summaryFeedback === 'negative'
                                    ? 'material-icons'
                                    : 'material-icons-outlined'
                            "
                            >thumb_down</span
                        >
                    </button>
                </div>
            </div>
        </template>

        <FeedbackDialog
            :visible="feedbackDialogOpen"
            @submit="onFeedbackSubmit"
            @skip="onFeedbackSkip"
        />
    </div>
</template>

<style scoped>
/* Subtle, non-blocking notice shown while the summary is being reconciled
   with reviewed entity/event changes (POST /MatterContext/UpdateSummary). */
.mc-summary-updating {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 12px;
    font-size: 13px;
    color: var(--lineal-red, #c0392b);
}

.mc-summary-updating-icon {
    font-size: 16px;
    animation: mc-summary-updating-pulse 1.2s ease-in-out infinite;
}

@keyframes mc-summary-updating-pulse {
    0%,
    100% {
        opacity: 0.45;
    }
    50% {
        opacity: 1;
    }
}

@media (prefers-reduced-motion: reduce) {
    .mc-summary-updating-icon {
        animation: none;
    }
}
</style>
